import React, { useCallback, useState, createContext, useContext } from 'react';
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, InfoIcon, XIcon } from 'lucide-react';
interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}
interface ToastContextType {
  showToast: (message: string, type: Toast['type'], duration?: number) => void;
  hideToast: (id: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((message: string, type: Toast['type'], duration = 5000) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = {
      id,
      type,
      message,
      duration
    };
    setToasts(prev => [...prev, newToast]);
    if (duration > 0) {
      setTimeout(() => hideToast(id), duration);
    }
  }, []);
  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  const getIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-success" data-id="element-586" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-danger" data-id="element-587" />;
      case 'warning':
        return <AlertCircleIcon className="w-5 h-5 text-warning" data-id="element-588" />;
      case 'info':
        return <InfoIcon className="w-5 h-5 text-info" data-id="element-589" />;
    }
  };
  const getStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-white border-success/20 shadow-level-2';
      case 'error':
        return 'bg-white border-danger/20 shadow-level-2';
      case 'warning':
        return 'bg-white border-warning/20 shadow-level-2';
      case 'info':
        return 'bg-white border-info/20 shadow-level-2';
    }
  };
  return <ToastContext.Provider value={{
    showToast,
    hideToast
  }} data-id="element-590">
      {children}
      {/* Toast Container with Smooth Animations */}
      <div className="fixed top-20 right-6 z-50 space-y-2 pointer-events-none" data-id="element-591">
        {toasts.map(toast => <div key={toast.id} className={`pointer-events-auto min-w-[320px] max-w-md rounded-xl border-2 p-4 flex items-start gap-3 animate-slide-in-right ${getStyles(toast.type)}`} role="alert" aria-live="assertive" style={{
        animation: 'slideInRight 250ms cubic-bezier(0.0, 0, 0.2, 1)'
      }} data-id="element-592">
            <div className="flex-shrink-0 transition-transform duration-200 hover:scale-110" data-id="element-593">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 text-sm font-medium text-gray-900" data-id="element-594">
              {toast.message}
            </div>
            <button onClick={() => hideToast(toast.id)} className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-all duration-200 hover:scale-110 active:scale-95 rounded-lg p-1 hover:bg-gray-100" aria-label="Dismiss" data-id="element-595">
              <XIcon className="w-4 h-4" data-id="element-596" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}